import { useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const router = useRouter();
  const pathname = router.state.location.pathname;
  
  // Check if on game page for different layout
  const isGamePage = pathname.startsWith('/games/');

  return (
    <div className="min-h-screen bg-bg-deep flex flex-col">
      <Header isGamePage={isGamePage} />
      <main className={cn(
        "flex-1",
        !isGamePage && "container mx-auto px-4 py-4 sm:py-8"
      )}>
        {children}
      </main>
      {!isGamePage && <Footer />}
    </div>
  );
}

function Header({ isGamePage }: { isGamePage: boolean }) {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <header className={cn(
      "sticky top-0 z-40 bg-bg-deep/80 backdrop-blur-lg border-b border-border-dark",
      isGamePage && "bg-bg-deep/95"
    )}>
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-ukonnx-green rounded-lg flex items-center justify-center
                          group-hover:scale-105 transition-transform">
              <span className="text-bg-deep font-bold text-lg sm:text-xl">U</span>
            </div>
            <div>
              <span className="font-bold text-text-primary text-sm sm:text-base">UKONNX</span>
              <span className="text-ukonnx-green font-semibold ml-1 text-sm sm:text-base">Games</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <NavLink to="/">{t.nav.home}</NavLink>
            <NavLink to="/leaderboard">{t.nav.leaderboard}</NavLink>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-text-secondary hover:text-text-primary transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border-dark animate-slide-up">
            <div className="py-4 space-y-1">
              <MobileNavLink to="/" onClick={() => setMobileMenuOpen(false)}>
                {t.nav.home}
              </MobileNavLink>
              <MobileNavLink to="/leaderboard" onClick={() => setMobileMenuOpen(false)}>
                {t.nav.leaderboard}
              </MobileNavLink>
              <div className="px-4 pt-3 border-t border-border-dark mt-3">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="text-text-secondary hover:text-text-primary transition-colors font-medium"
      activeProps={{ className: 'text-ukonnx-green' }}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ 
  to, 
  children, 
  onClick 
}: { 
  to: string; 
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Link
      to={to}
      className="block px-4 py-3 text-text-secondary hover:text-text-primary hover:bg-bg-container 
                 transition-colors font-medium rounded-lg"
      activeProps={{ className: 'text-ukonnx-green bg-ukonnx-green/10' }}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function Footer() {
  // Format build time for display (e.g., "25.01.2026 21:30")
  const buildDate = new Date(__BUILD_TIME__);
  const formattedDate = buildDate.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
  });
  const formattedTime = buildDate.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <footer className="border-t border-border-dark py-4 sm:py-6 mt-auto">
      <div className="container mx-auto px-4 text-center text-text-muted text-xs sm:text-sm">
        <p>Powered by <span className="text-ukonnx-green font-semibold">UKONNX</span></p>
        <p className="mt-1">Industrial Automation Made Fun</p>
        <p className="mt-2 text-text-muted/60 font-mono text-[10px] sm:text-xs">
          v{__APP_VERSION__} • {__GIT_HASH__} • {formattedDate} {formattedTime}
        </p>
      </div>
    </footer>
  );
}
